// Centralized "whose turn is it?" logic. Returns the role of the next actor
// for a given row, or null if the row is in a settled state (no action needed).
//
// Roles: "staff" | "bookkeeper" | "owner"
//
// Keep this pure — no React, no API. Easy to test, easy to reuse.

export function nextActorForMonthlyItem(item) {
  if (!item) return null;

  // Tax decisions follow the three-state flow we just shipped.
  if (item.category === "decision") {
    if (item.status === "pending") return "bookkeeper";        // bookkeeper preparing the amount
    if (item.status === "awaiting_approval") return "owner";   // owner deciding yes/no
    if (item.status === "submitted" && item.decision === "pay" && !item.payment_confirmation && !item.payment_confirmation_document_id) {
      return "bookkeeper";                                     // bookkeeper still owes the receipt (text or upload)
    }
    return null;                                               // fully done (paid + receipt, OR do_not_pay)
  }

  // Regular monthly statements/documents — staff uploads them.
  if (item.status === "pending") return "staff";
  return null;
}

export function nextActorForRemittance(adv) {
  if (!adv) return null;

  // Cleared in QBO and on bank statement → done.
  if (adv.qbo_payment_id && adv.qbo_cleared_at) return null;

  // Manually flagged or auto-flagged for review → bookkeeper handles.
  if (adv.needs_review) return "bookkeeper";

  // Posted to QBO but not yet cleared → bookkeeper reconciles via bank statement.
  if (adv.qbo_payment_id && !adv.qbo_cleared_at) return "bookkeeper";

  // Status flags we may set elsewhere
  if (adv.status === "applied" && !adv.qbo_cleared_at) return "bookkeeper";

  // Default new/pending advices → staff matches them.
  return "staff";
}

// UI tokens for each role. Colors picked by the owner: purple=staff, green=bookkeeper, blue=owner.
export const ROLE_TOKENS = {
  staff: {
    label: "Yours",
    longLabel: "Yours (admin)",
    color: "#7C3AED",        // purple-600
    soft: "#7C3AED14",       // ~8% alpha
    softer: "#7C3AED0A",     // ~4% alpha
  },
  bookkeeper: {
    label: "Bookkeeper",
    longLabel: "Bookkeeper",
    color: "#10B981",        // emerald-500
    soft: "#10B98114",
    softer: "#10B9810A",
  },
  owner: {
    label: "Owner",
    longLabel: "Owner",
    color: "#002FA7",        // IKB blue
    soft: "#002FA714",
    softer: "#002FA70A",
  },
};

// "Whose tasks should I see?" — friendly label for the filter pill
export function roleFilterLabelFor(viewerRole) {
  if (viewerRole === "staff") return "Yours";
  if (viewerRole === "bookkeeper") return "Yours";
  if (viewerRole === "owner") return "Yours";
  return "Yours";
}

// Hide-from-Monthly-Items detector (2026-05-28): bank-statement rows live
// on the Remittances page workflow now, so we drop them from the recurring
// checklist UI to keep the dyslexic-friendly list short and focused. The
// templates still exist in the DB so the auto-clear pipeline can find them
// — this filter is purely cosmetic in the React layer.
//
// Mirrors the backend's `_is_bank_statement_item` in server.py.
export function isBankStatementItem(item) {
  if (!item) return false;
  const haystack = `${item.vendor || ""} ${item.name || ""}`.toLowerCase();
  if (!haystack.trim()) return false;
  return /\b(bank|checking|operating account)\b/.test(haystack);
}

// Map the viewer's role → the "next actor" role bucket they should consider their own.
export function ownBucketFor(viewerRole) {
  if (viewerRole === "staff") return "staff";
  if (viewerRole === "bookkeeper") return "bookkeeper";
  if (viewerRole === "owner") return "owner";
  return null;
}
